class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.pendingNativeAdds = [];
    this.onNativeCartDisplay = this.handleNativeCartDisplay.bind(this);
    document.addEventListener('shopify:cart:lines-update', this.onNativeCartDisplay);
    document.addEventListener('click', this.captureCartDisplayIntent.bind(this), true);
    this.showCartRedirectConfirmation();

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    const ids = (parsedState.items || [parsedState]).map(item => String(item.variant_id || item.id));
    const pending = [...this.pendingNativeAdds].reverse().find(record =>
      !record.fastCard && record.lines.some(line => ids.includes(String(line.merchandiseId).split('/').pop()))
    );
    if (pending) {
      pending.added = parsedState;
      return;
    }
    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  captureCartDisplayIntent(event) {
    const button = event.target?.closest?.('product-form button[type="submit"]');
    const form = button?.closest('form');
    const quantityInput = form?.elements.namedItem('quantity');
    if (!quantityInput?.hasAttribute('data-cart-quantity')) return;
    const variant = form.elements.namedItem('id')?.value;
    const beforeQuantity = Number(quantityInput.dataset.cartQuantity);
    const quantity = Number(quantityInput.value);
    if (!variant || !Number.isFinite(beforeQuantity) || !Number.isFinite(quantity) || quantity < 1) return;
    try {
      sessionStorage.setItem('cbrn-cart-display-intent', JSON.stringify({
        variant, beforeQuantity, quantity, returnPath: window.location.pathname, createdAt: Date.now(),
      }));
    } catch {}
  }

  showCartRedirectConfirmation() {
    const state = this.querySelector('[data-cart-display-state]');
    if (!state) return;
    try {
      const intent = JSON.parse(sessionStorage.getItem('cbrn-cart-display-intent'));
      sessionStorage.removeItem('cbrn-cart-display-intent');
      if (!intent || Date.now() - intent.createdAt > 600000) return;
      const cart = JSON.parse(state.textContent);
      const item = cart.items.find(line => String(line.variant_id) === intent.variant);
      const quantity = item && Math.min(intent.quantity, item.quantity - intent.beforeQuantity);
      const form = document.getElementById('cart');
      if (!quantity || quantity < 1 || !form) return;
      this.showInlineConfirmation({ ...item, quantity }, form.parentElement, quantity, cart, null);
      form.parentElement.insertBefore(this.inlineConfirmation, form);
      this.inlineConfirmation.querySelector('a').setAttribute('href', '#cart');
      if (intent.returnPath?.startsWith('/') && !intent.returnPath.startsWith('//')) {
        this.inlineReturnPath = intent.returnPath;
      }
    } catch {}
  }

  handleNativeCartDisplay(event) {
    const source = event.target?.closest?.('product-form');
    if (!source || event.action !== 'add' || !event.promise?.then) return;
    const modal = source.closest('quick-add-modal');
    const target = modal ? modal.openedBy?.closest('.quick-add') : source;
    if (!target) return;
    if (this.inlineConfirmation) this.inlineConfirmation.hidden = true;
    const titleElement = source.closest('.card-wrapper')?.querySelector('.card__heading a') ||
      source.closest('product-info')?.querySelector('h1') || document.querySelector('main h1');
    const token = (this.nativeDisplayToken || 0) + 1;
    this.nativeDisplayToken = token;
    const record = {
      lines: event.lines,
      target,
      token,
      title: titleElement?.textContent.trim() || '',
      button: modal?.openedBy || source.querySelector('[type="submit"]'),
      fastCard: source.dataset.fastCart === 'true' && !modal,
    };
    this.pendingNativeAdds.push(record);
    event.promise.then(result => {
      if (record.token !== this.nativeDisplayToken || result.userErrors?.length || result.warnings?.length) return;
      const cart = result.cart;
      const total = cart?.cost?.totalAmount;
      if (!total?.currencyCode || !Number.isFinite(Number(total.amount))) return;
      const responseItems = record.added ? (record.added.items || [record.added]) : [];
      const items = record.lines.map(request => {
        const id = String(request.merchandiseId).split('/').pop();
        const response = responseItems.find(item => String(item.variant_id || item.id) === id);
        const line = cart.lines.find(item => item.id === response?.key || String(item.id).split(':')[0] === id);
        if (!line?.quantity || !Number.isFinite(Number(line.cost?.totalAmount?.amount))) return null;
        return {
          key: line.id,
          variant_id: id,
          title: response?.title || record.title,
          quantity: request.quantity,
          final_price: Math.round(Number(line.cost.totalAmount.amount) * 100 / line.quantity),
        };
      }).filter(Boolean);
      if (!items.length) return;
      const confirmed = { currency: total.currencyCode, total_price: Math.round(Number(total.amount) * 100), items };
      this.showInlineConfirmation(
        { items }, record.target, items[0].quantity, confirmed, record.button
      );
    }).catch(() => {}).finally(() => {
      this.pendingNativeAdds = this.pendingNativeAdds.filter(item => item !== record);
    });
  }

  showInlineConfirmation(added, target, quantity, cartData, submitButton) {
    const template = this.querySelector('[data-inline-confirmation]');
    if (!template || !target) return;
    const token = (this.inlineToken || 0) + 1;
    this.inlineToken = token;
    this.inlineReturnPath = null;
    if (!this.inlineConfirmation) {
      this.inlineConfirmation = template.content.firstElementChild.cloneNode(true);
      this.inlineConfirmation.querySelector('[data-keep-shopping]').addEventListener('click', () => {
        if (this.inlineReturnPath) {
          window.location.assign(this.inlineReturnPath);
          return;
        }
        this.inlineConfirmation.hidden = true;
        if (this.inlineSubmitButton?.isConnected) this.inlineSubmitButton.focus({ preventScroll: true });
      });
    }
    this.inlineSubmitButton = submitButton;
    if (this.notification.classList.contains('active')) this.close();
    const items = added.items || [added];
    const currency = this.dataset.cartCurrency;
    this.renderInlineItems(items, quantity, currency);
    this.inlineConfirmation.querySelector('[data-cart-subtotal-row]').hidden = true;
    this.inlineConfirmation.hidden = false;
    target.appendChild(this.inlineConfirmation);

    Promise.resolve(cartData)
      .then((cart) => {
        if (this.inlineToken !== token || !cart?.currency) return;
        const confirmedItems = items.map((item) =>
          cart.items.find((line) => line.key === item.key) ||
          cart.items.find((line) => line.variant_id === item.variant_id) || item
        );
        this.renderInlineItems(confirmedItems, quantity, cart.currency);
        this.inlineConfirmation.querySelector('[data-cart-subtotal]').replaceChildren(
          this.createInlineMoney(cart.total_price, cart.currency)
        );
        this.inlineConfirmation.querySelector('[data-cart-subtotal-row]').hidden = false;
      })
      .catch(() => {});
  }

  renderInlineItems(items, quantity, currency) {
    const list = this.inlineConfirmation.querySelector('[data-added-items]');
    list.replaceChildren();
    for (const item of items) {
      const row = document.createElement('li');
      const title = document.createElement('span');
      title.className = 'cbrn-cart-confirmation__title';
      title.textContent = item.title || item.product_title;
      const details = document.createElement('span');
      details.className = 'cbrn-cart-confirmation__details';
      const addedQuantity = items.length === 1 ? Math.min(quantity, item.quantity) : item.quantity;
      const count = document.createElement('span');
      count.textContent = `${this.dataset.quantityLabel}: ${addedQuantity}`;
      details.appendChild(count);
      const price = document.createElement('span');
      const [before, after = ''] = this.dataset.priceEach.split('__PRICE__');
      price.append(before, this.createInlineMoney(item.final_price ?? item.price, currency), after);
      details.appendChild(price);
      row.append(title, details);
      list.appendChild(row);
    }
  }

  createInlineMoney(cents, currency) {
    const span = document.createElement('span');
    span.className = 'money';
    const format = this.dataset.moneyFormat;
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    const rule = match && formats[match[1]];
    if (rule && currency === this.dataset.cartCurrency) {
      const [precision, thousands, decimal] = rule;
      const [whole, fraction] = (Number(cents) / 100).toFixed(precision).split('.');
      const amount = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      span.textContent = format.replace(match[0], amount);
    } else {
      span.textContent = new Intl.NumberFormat(document.documentElement.lang, {
        style: 'currency', currency, currencyDisplay: 'code',
      }).format(Number(cents) / 100);
    }
    return span;
  }
}

customElements.define('cart-notification', CartNotification);

